import { useState, useEffect } from "react";
import axios from "axios";
import {
  Storefront,
  Plus,
  Pencil,
  MagnifyingGlass,
  Phone,
  MapPin,
  X,
  Prohibit,
  ArrowCounterClockwise,
} from "@phosphor-icons/react";
import Layout from "../components/Layout";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function StoresPage() {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingStore, setEditingStore] = useState(null);
  const [search, setSearch] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    cnpj: "",
    address: "",
    city: "",
    state: "",
    phone: "",
  });

  useEffect(() => {
    fetchStores();
  }, []);

  const fetchStores = async () => {
    try {
      const response = await axios.get(`${API}/stores?active_only=false`, {
        withCredentials: true,
      });
      setStores(response.data);
    } catch (error) {
      console.error("Error fetching stores:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingStore) {
        await axios.put(`${API}/stores/${editingStore.id}`, formData, {
          withCredentials: true,
        });
        toast.success("Loja atualizada com sucesso!");
      } else {
        await axios.post(`${API}/stores`, formData, { withCredentials: true });
        toast.success("Loja cadastrada com sucesso!");
      }

      setShowForm(false);
      setEditingStore(null);
      setFormData({
        name: "",
        cnpj: "",
        address: "",
        city: "",
        state: "",
        phone: "",
      });
      fetchStores();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao salvar loja");
    }
  };

  const handleEdit = (store) => {
    setEditingStore(store);
    setFormData({
      name: store.name || "",
      cnpj: store.cnpj || "",
      address: store.address || "",
      city: store.city || "",
      state: store.state || "",
      phone: store.phone || "",
    });
    setShowForm(true);
  };

  const handleToggleActive = (item) => {
    const isCurrentlyActive = item.active !== false;
    const actionText = isCurrentlyActive ? "desativar" : "reativar";

    toast(`${isCurrentlyActive ? "Desativar" : "Reativar"} loja?`, {
      description: `Deseja realmente ${actionText} "${item.name}"?`,
      action: {
        label: `Sim, ${actionText}`,
        onClick: async () => {
          try {
            if (isCurrentlyActive) {
              await axios.delete(`${API}/stores/${item.id}`, {
                withCredentials: true,
              });
            } else {
              await axios.put(
                `${API}/stores/${item.id}`,
                { active: true },
                {
                  withCredentials: true,
                },
              );
            }
            toast.success(
              `Loja ${isCurrentlyActive ? "desativada" : "reativada"}!`,
            );
            fetchStores();
          } catch (error) {
            console.log(error);
            toast.error(`Erro ao ${actionText}`);
          }
        },
      },
      cancel: {
        label: "Cancelar",
      },
      duration: 5000,
    });
  };

  const filteredStores = stores.filter(
    (s) =>
      s.name.toLowerCase().includes(search.toLowerCase()) ||
      (s.city && s.city.toLowerCase().includes(search.toLowerCase())),
  );

  if (loading) {
    return (
      <Layout title="Lojas">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-2 border-[#00E676] border-t-transparent rounded-full"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Lojas">
      <div className="space-y-4 pb-4">
        {/* Search */}
        <div className="relative w-full">
          <MagnifyingGlass
            size={20}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A1A1AA] shrink-0"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input-field !pl-12 h-12 w-full"
            placeholder="Buscar loja..."
            data-testid="search-stores"
          />
        </div>

        {/* Stores List */}
        {filteredStores.length === 0 ? (
          <div className="card p-8 text-center">
            <Storefront size={48} className="mx-auto text-[#A1A1AA] mb-4" />
            <p className="text-[#A1A1AA] mb-4">Nenhuma loja cadastrada</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredStores.map((store) => (
              <div
                key={store.id}
                className={`card p-4 sm:p-5 transition-all ${store.active === false ? "opacity-50 grayscale-[50%]" : ""}`}
                data-testid={`store-${store.id}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-[#00E676]/10 flex items-center justify-center flex-shrink-0">
                      <Storefront
                        size={20}
                        className="text-[#00E676] sm:w-6 sm:h-6"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-bold text-base sm:text-lg truncate flex items-center flex-wrap gap-2">
                        {store.name}

                        {store.active === false && (
                          <span className="text-[10px] uppercase font-bold bg-red-500/20 text-red-500 px-2 py-0.5 rounded border border-red-500/20">
                            Desativada
                          </span>
                        )}
                      </h3>
                      {store.cnpj && (
                        <p className="text-xs text-[#A1A1AA] font-mono mt-0.5">
                          {store.cnpj}
                        </p>
                      )}
                      {(store.city || store.state) && (
                        <div className="flex items-center gap-1.5 mt-1.5 text-xs text-[#A1A1AA] truncate">
                          <MapPin size={14} className="shrink-0" />
                          <span className="truncate">
                            {[store.city, store.state]
                              .filter(Boolean)
                              .join(" - ")}
                          </span>
                        </div>
                      )}
                      {store.phone && (
                        <div className="flex items-center gap-1.5 mt-1 text-xs text-[#A1A1AA] truncate">
                          <Phone size={14} className="shrink-0" />
                          <span className="truncate">{store.phone}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-2 shrink-0 border-t border-white/5 sm:border-0 pt-3 sm:pt-0">
                    <button
                      onClick={() => handleEdit(store)}
                      className="w-10 h-10 sm:w-auto sm:px-4 rounded-lg bg-[#1A1A1A] border border-white/10 flex items-center justify-center hover:bg-[#222] transition-colors gap-2"
                      data-testid={`edit-store-${store.id}`}
                    >
                      <Pencil size={18} className="text-[#A1A1AA]" />
                      <span className="hidden sm:inline text-sm text-[#A1A1AA]">
                        Editar
                      </span>
                    </button>

                    <button
                      onClick={() => handleToggleActive(store)}
                      className={`w-10 h-10 sm:w-auto sm:px-4 rounded-lg border flex items-center justify-center transition-colors gap-2 ${
                        store.active !== false
                          ? "bg-red-500/10 border-red-500/20 hover:bg-red-500/20 text-red-500"
                          : "bg-blue-500/10 border-blue-500/20 hover:bg-blue-500/20 text-blue-500"
                      }`}
                    >
                      {store.active !== false ? (
                        <>
                          <Prohibit size={18} />
                          <span className="hidden sm:inline text-sm">
                            Desativar
                          </span>
                        </>
                      ) : (
                        <>
                          <ArrowCounterClockwise size={18} />
                          <span className="hidden sm:inline text-sm">
                            Reativar
                          </span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* FAB */}
        <button
          onClick={() => {
            setEditingStore(null);
            setFormData({
              name: "",
              cnpj: "",
              address: "",
              city: "",
              state: "",
              phone: "",
            });
            setShowForm(true);
          }}
          className="fixed bottom-24 right-4 w-14 h-14 bg-[#00E676] rounded-full flex items-center justify-center shadow-[0_0_15px_rgba(0,230,118,0.3)] hover:bg-[#00C853] hover:scale-105 transition-all z-20"
          data-testid="add-store-button"
        >
          <Plus size={28} weight="bold" className="text-black" />
        </button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-[#121212] rounded-xl p-5 sm:p-6 w-full max-w-md max-h-[90vh] overflow-y-auto border border-white/10 shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-lg">
                {editingStore ? "Editar Loja" : "Nova Loja"}
              </h3>
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditingStore(null);
                }}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#1A1A1A] transition-colors"
              >
                <X size={20} className="text-[#A1A1AA]" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-xs text-[#A1A1AA] block mb-1.5 ml-1">
                  Nome *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="input-field w-full"
                  placeholder="Nome da loja"
                  required
                  data-testid="store-name-input"
                />
              </div>

              <div>
                <label className="text-xs text-[#A1A1AA] block mb-1.5 ml-1">
                  CNPJ
                </label>
                <input
                  type="text"
                  value={formData.cnpj}
                  onChange={(e) =>
                    setFormData({ ...formData, cnpj: e.target.value })
                  }
                  className="input-field w-full font-mono text-sm"
                  placeholder="00.000.000/0000-00"
                  data-testid="store-cnpj-input"
                />
              </div>

              <div>
                <label className="text-xs text-[#A1A1AA] block mb-1.5 ml-1">
                  Endereço
                </label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) =>
                    setFormData({ ...formData, address: e.target.value })
                  }
                  className="input-field w-full"
                  placeholder="Rua, número"
                  data-testid="store-address-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#A1A1AA] block mb-1.5 ml-1">
                    Cidade
                  </label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) =>
                      setFormData({ ...formData, city: e.target.value })
                    }
                    className="input-field w-full"
                    placeholder="Cidade"
                    data-testid="store-city-input"
                  />
                </div>
                <div>
                  <label className="text-xs text-[#A1A1AA] block mb-1.5 ml-1">
                    Estado
                  </label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        state: e.target.value.toUpperCase(),
                      })
                    }
                    className="input-field w-full uppercase"
                    placeholder="UF"
                    maxLength={2}
                    data-testid="store-state-input"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs text-[#A1A1AA] block mb-1.5 ml-1">
                  Telefone
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                  className="input-field w-full"
                  placeholder="(00) 00000-0000"
                  data-testid="store-phone-input"
                />
              </div>

              <button
                type="submit"
                className="btn-primary w-full mt-6 py-3"
                data-testid="save-store-button"
              >
                {editingStore ? "Salvar Alterações" : "Cadastrar Loja"}
              </button>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
